import { useState, useMemo } from "react";
import { Trophy, ArrowUp, ArrowDown, ArrowUpDown, Search } from "lucide-react";

const KIND_BADGE = {
  standings: "bg-[#1E63C4] text-white",
  stats: "bg-[#62A8E5] text-white",
  schedule: "bg-[#E11D2A] text-[#111111]",
  teams: "bg-gray-700 text-white",
  list: "bg-[#0B7285] text-white",
  champions: "bg-amber-500 text-white",
  general: "bg-gray-200 text-gray-800",
};

const isTeamHeaderRow = (row) => row && row[0] && row.slice(1).every((c) => !c || String(c).trim() === "");

const parseScheduleDate = (s) => {
  const m = String(s ?? "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    let y = +m[3];
    if (y < 100) y += 2000;
    return new Date(y, +m[1] - 1, +m[2]).getTime();
  }
  const d = Date.parse(s);
  return isNaN(d) ? Infinity : d;
};

export function DataTableView({ table, stamp }) {
  const { title, kind, columns, rows, note } = table;
  const isStandings = kind === "standings";
  const isTeams = kind === "teams";
  const isSchedule = kind === "schedule";
  const interactive = kind === "stats" || kind === "standings" || kind === "list";

  const [query, setQuery] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const displayed = useMemo(() => {
    let out = rows || [];
    // Schedules always render by date, latest → earliest (unparseable last).
    if (isSchedule) {
      out = [...out].sort((a, b) => {
        const da = parseScheduleDate(a?.[0]), db = parseScheduleDate(b?.[0]);
        if (da === Infinity && db === Infinity) return 0;
        if (da === Infinity) return 1;
        if (db === Infinity) return -1;
        return db - da;
      });
    }
    if (interactive && query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter((r) => r.some((c) => String(c ?? "").toLowerCase().includes(q)));
    }
    if (interactive && sortCol !== null) {
      out = [...out].sort((a, b) => {
        const av = a[sortCol] ?? "", bv = b[sortCol] ?? "";
        const an = parseFloat(String(av).replace(/[^0-9.-]/g, ""));
        const bn = parseFloat(String(bv).replace(/[^0-9.-]/g, ""));
        let r;
        if (!isNaN(an) && !isNaN(bn) && String(av).trim() !== "" && String(bv).trim() !== "") r = an - bn;
        else r = String(av).localeCompare(String(bv));
        return sortDir === "asc" ? r : -r;
      });
    }
    return out;
  }, [rows, query, sortCol, sortDir, interactive, isSchedule]);

  const toggleSort = (ci) => {
    if (sortCol === ci) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(ci); setSortDir("asc"); }
  };

  const hasData = columns && columns.length > 0 && rows && rows.length > 0;

  return (
    <div className="mb-10" data-testid={`table-${kind}`}>
      <div className="flex items-center flex-wrap gap-3 mb-3">
        <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#111111]">{title}</h3>
        <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${KIND_BADGE[kind] || KIND_BADGE.general}`}>
          {kind}
        </span>
        {stamp && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500 text-white" data-testid="table-stamp">
            <Trophy className="h-3 w-3" /> {stamp}
          </span>
        )}
        {interactive && hasData && (
          <div className="relative ml-auto">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#999999]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter…"
              data-testid={`table-filter-${table.id}`}
              className="w-44 sm:w-56 rounded-md border border-[#E5E7EB] pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
            />
          </div>
        )}
      </div>
      {note && <p className="text-sm text-[#555555] mb-3">{note}</p>}

      {!hasData ? (
        <p className="text-sm text-[#555555] italic">No data has been posted yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg shadow-sm border border-[#E5E7EB] bg-white">
          <table className="w-full text-left border-collapse text-sm" data-testid={`data-table-${table.id}`}>
            <thead>
              <tr className="bg-[#1E63C4] text-white">
                {columns.map((c, i) => (
                  <th
                    key={i}
                    onClick={interactive ? () => toggleSort(i) : undefined}
                    data-testid={interactive ? `sort-col-${table.id}-${i}` : undefined}
                    className={`px-4 py-3 font-heading font-semibold whitespace-nowrap text-sm ${interactive ? "cursor-pointer select-none hover:bg-[#174e9c]" : ""}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c}
                      {interactive && (sortCol === i ? (sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr><td colSpan={columns.length} className="px-4 py-6 text-center text-[#555555] italic">No matches for “{query}”.</td></tr>
              ) : (
                displayed.map((row, ri) => {
                  if (isTeams && isTeamHeaderRow(row)) {
                    return (
                      <tr key={ri} className="bg-[#1E63C4]/10 border-y border-[#1E63C4]/20" data-testid="team-header-row">
                        <td colSpan={columns.length} className="px-4 py-2.5 font-heading font-bold text-[#1E63C4] text-base">
                          {row[0]}
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={ri} className={`border-b border-[#E5E7EB] transition-colors hover:bg-blue-50 ${ri % 2 === 1 ? "bg-gray-50" : "bg-white"}`}>
                      {columns.map((_, ci) => (
                        <td key={ci} className={`px-4 py-3 text-[#333333] whitespace-nowrap ${isTeams && ci === 0 ? "pl-8" : ""}`}>
                          {isStandings && ci === 0 && row[ci] === "1" ? (
                            <span className="inline-flex items-center gap-1 font-semibold text-[#1E63C4]">
                              <Trophy className="h-4 w-4 text-[#E11D2A]" /> {row[ci]}
                            </span>
                          ) : (
                            row[ci] ?? ""
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
